import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import {
  FiCalendar,
  FiClock,
  FiEye,
  FiUser,
  FiTrendingUp,
  FiBarChart2,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiSearch,
  FiRefreshCw
} from "react-icons/fi";

const Views = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    propertyId: "",
    userId: "",
    startDate: "",
    endDate: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const res = await axios.get("/properties/analytics", { params });
      setAnalytics(res.data);
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchAnalytics();
  };

  const clearFilters = () => {
    setFilters({ propertyId: "", userId: "", startDate: "", endDate: "" });
    setCurrentPage(1);
    setTimeout(fetchAnalytics, 100);
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-500 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentLogs = analytics?.logs.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil((analytics?.logs.length || 0) / rowsPerPage);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="flex h-screen  bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-y-auto max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            Property View Analytics
          </h1>
          <p className="text-gray-500 text-sm md:text-base">
            Track supervisor engagement and property inspection patterns
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
            >
              <FiFilter size={18} />
              <span>Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="bg-teal-100 text-teal-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            {Object.values(filters).filter(Boolean).length > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <FiRefreshCw size={14} className="mr-1" />
                Clear all
              </button>
            )}
          </div>

          {showFilters && (
            <div className="p-4 bg-gray-50 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Property ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="text-gray-400" size={16} />
                    </div>
                    <input
                      type="text"
                      value={filters.propertyId}
                      onChange={(e) => handleFilterChange("propertyId", e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Enter property ID"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-400" size={16} />
                    </div>
                    <input
                      type="text"
                      value={filters.userId}
                      onChange={(e) => handleFilterChange("userId", e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="Enter user ID"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiCalendar className="text-gray-400" size={16} />
                    </div>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange("startDate", e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiCalendar className="text-gray-400" size={16} />
                    </div>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange("endDate", e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition flex items-center gap-2 shadow-sm"
                >
                  <FiSearch size={16} />
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {analytics && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 hover:shadow-md transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Total Views</p>
                    <p className="text-2xl md:text-3xl font-bold text-teal-600">
                      {analytics.summary.totalViews.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-teal-50 p-3 rounded-full">
                    <FiEye className="text-teal-500" size={22} />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Across all properties</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 hover:shadow-md transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Total Time Spent</p>
                    <p className="text-2xl md:text-3xl font-bold text-blue-600">
                      {formatTime(analytics.summary.totalTimeSpent)}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-full">
                    <FiClock className="text-blue-500" size={22} />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Cumulative viewing time</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-100 hover:shadow-md transition">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm font-medium mb-1">Average Time</p>
                    <p className="text-2xl md:text-3xl font-bold text-purple-600">
                      {formatTime(analytics.summary.averageTimeSpent)}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-full">
                    <FiTrendingUp className="text-purple-500" size={22} />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Per property view</p>
                </div>
              </div>
            </div>

            {/* Logs Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
              <div className="p-5 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FiBarChart2 size={20} /> Detailed View Logs
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                  Recent property view activities and time spent
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Property", "User", "Time Spent", "Viewed At", "Circle"].map(
                        (header) => (
                          <th
                            key={header}
                            className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.pin}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              ID: {log.property_id}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-gray-100 rounded-full p-2 mr-3">
                              <FiUser size={14} className="text-gray-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {log.username}
                              </div>
                              <div className="text-xs text-gray-500 capitalize">{log.role}</div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                              log.time_spent_seconds >= 120
                                ? "bg-green-100 text-green-800"
                                : log.time_spent_seconds >= 60
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {formatTime(log.time_spent_seconds)}
                          </span>
                        </td>

                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <FiCalendar size={14} className="text-gray-400 mr-2" />
                            {formatDate(log.viewed_at)}
                          </div>
                        </td>

                        <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-teal-100 text-teal-800">
                            {log.circle}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {analytics.logs.length > rowsPerPage && (
                <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200 bg-gray-50 gap-4">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{indexOfFirstRow + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(indexOfLastRow, analytics.logs.length)}
                    </span>{" "}
                    of <span className="font-medium">{analytics.logs.length}</span> results
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      <FiChevronLeft size={16} /> Prev
                    </button>
                    
                    {getPageNumbers().map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 flex items-center justify-center text-sm rounded-md ${
                          currentPage === page
                            ? "bg-teal-500 text-white"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded-md shadow-sm text-sm disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      Next <FiChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {analytics.logs.length === 0 && (
                <div className="text-center py-12">
                  <FiBarChart2 className="mx-auto h-12 w-12 text-gray-300" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No data found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 mb-4">
                    No property views match your current filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Views;